import React, { createContext, useContext, useState, useCallback } from 'react';
import NotificationPopup from '../components/NotificationPopup';

// Create context
const NotificationContext = createContext();

// Custom hook to use notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification provider component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @returns {JSX.Element} - Notification provider
 */
export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    type: 'info',
    message: '',
    autoHideDuration: 5000,
  });

  // Show success notification
  const showSuccess = useCallback((message, autoHideDuration = 4000) => {
    setNotification({
      open: true,
      type: 'success',
      message,
      autoHideDuration,
    });
  }, []);

  // Show error notification
  const showError = useCallback((message, autoHideDuration = 6000) => {
    setNotification({
      open: true,
      type: 'error',
      message,
      autoHideDuration,
    });
  }, []);

  // Show info notification
  const showInfo = useCallback((message, autoHideDuration = 5000) => {
    setNotification({
      open: true,
      type: 'info',
      message,
      autoHideDuration,
    });
  }, []);

  // Show warning notification
  const showWarning = useCallback((message, autoHideDuration = 6000) => {
    setNotification({
      open: true,
      type: 'warning',
      message,
      autoHideDuration,
    });
  }, []);

  // Close notification
  const closeNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  // Context value
  const value = {
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationPopup
        open={notification.open}
        type={notification.type}
        message={notification.message}
        autoHideDuration={notification.autoHideDuration}
        onClose={closeNotification}
      />
    </NotificationContext.Provider>
  );
};

export default NotificationContext; 